import React, { useEffect, useState } from "react";
import { Select, Modal, Button, Form, Input, message, AutoComplete } from "antd";
import { User, Companies } from "../../api";
import { useGlobal } from "../../global/user";

const { Option } = Select;

export default function UserModal({ value, visible, setVisible, setUsers, companies: propCompanies, setCompanies: propSetCompanies, refreshCompanies }) {
  const { user: currentUser } = useGlobal();
  const [form] = Form.useForm();
  const [localCompanies, setLocalCompanies] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [currentUserCompany, setCurrentUserCompany] = useState("");
  
  // 使用傳入的 companies 或本地 state
  const companies = propCompanies || localCompanies;
  const setCompanies = propSetCompanies || setLocalCompanies;
  
  // 判斷當前用戶是否為管理員（class >= 4）
  const isManager = currentUser.class >= parseInt(process.env.REACT_APP_MANAGER_CALSS_LV || "4");

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        // 每次打開 Modal 時，都從 API 獲取最新的公司列表
        const response = await Companies.getAllCompanies();
        const validCompanies = response.filter(c => c && c.id && c.company_name);
        
        // 更新父組件的公司列表（如果有提供 setCompanies）
        if (propSetCompanies) {
          propSetCompanies(validCompanies);
        }
        setCompanies(validCompanies);
        
        // 根據權限過濾公司列表
        let filteredCompanies = validCompanies;
        if (!isManager) {
          // 非管理員只能看到自己的公司
          filteredCompanies = validCompanies.filter(
            (company) => company && company.id === currentUser.company_id
          );
          // 保存當前用戶的公司名稱
          if (filteredCompanies.length > 0) {
            setCurrentUserCompany(filteredCompanies[0].company_name);
          }
        }
        
        // 設置 AutoComplete 的選項，過濾掉無效數據
        const options = filteredCompanies
          .filter(company => company && company.company_name)
          .map(company => ({
            value: company.company_name,
            label: company.company_name,
          }));
        setCompanyOptions(options);
        
        // 如果是非管理員且是新增使用者，自動設置公司
        if (!isManager && !value && filteredCompanies.length > 0) {
          form.setFieldsValue({
            company: filteredCompanies[0].company_name,
          });
        }
      } catch (error) {
        console.error("獲取公司列表失敗:", error);
        message.error("獲取公司列表失敗");
      }
    };

    if (visible) {
      fetchCompanies();
    }
  }, [visible, isManager, currentUser.company_id, value, form, propSetCompanies]);

  useEffect(() => {
    if (visible && value) {
      form.setFieldsValue(value);
    }
  }, [value, form, visible]);

  const onCancel = () => {
    form.resetFields(); // 使用 resetFields 而不是 setFieldsValue
    setVisible(false);
  };

  const DeleteUser = async () => {
    try {
      const response = await User.DeleteUser(value.id);
      if (response.code === 200) {
        setUsers((data) => data.filter((user) => user.id !== value.id));
        message.success("使用者刪除成功");
        setVisible(false);
      } else {
        message.error("刪除失敗");
      }
    } catch (error) {
      console.error("Delete user failed:", error);
      message.error("刪除失敗");
    }
  };

  const confirm = () => {
    Modal.confirm({
      title: "確認",
      content: "確認刪除使用者",
      okText: "確認",
      cancelText: "取消",
      onOk: DeleteUser,
    });
  };

  const onSubmit = async (values) => {
    let createdNewCompany = false; // 標記是否創建了新公司
    try {
      let response;
      if (value) {
        // 更新使用者
        const userObject = {
          ...values,
          user_id: value.id,
        };
        response = await User.UpdateUser(userObject);
      } else {
        // 創建使用者前，先檢查公司是否存在
        const companyName = values.company?.trim(); // 移除前後空格
        
        if (!companyName) {
          message.error("請輸入公司名稱");
          return;
        }
        
        console.log("準備創建使用者，公司名稱:", companyName);
        console.log("當前公司列表:", companies.map(c => c?.company_name));
        
        const companyExists = companies.some(
          (company) => company && company.company_name === companyName
        );

        if (!companyExists) {
          // 公司不存在，先創建公司
          try {
            message.loading({ content: `正在創建新公司「${companyName}」...`, key: 'createCompany' });
            const newCompany = await Companies.createCompany({
              company_name: companyName,
              theme_name: "basic", // 使用預設主題
              is_active: true,
            });
            
            console.log("公司創建成功:", newCompany);
            
            // 更新公司列表（確保 newCompany 有效）
            if (newCompany && newCompany.id && newCompany.company_name) {
              setCompanies((prev) => [...prev, newCompany]);
              createdNewCompany = true; // 標記已創建新公司
              message.success({ content: `公司「${companyName}」創建成功`, key: 'createCompany', duration: 2 });
              
              // 發出自定義事件通知 CompanyTable 刷新
              window.dispatchEvent(new CustomEvent('companyCreated', { detail: newCompany }));
            } else {
              throw new Error("創建公司返回的數據不完整");
            }
          } catch (companyError) {
            console.error("創建公司失敗:", companyError);
            message.error(`創建公司失敗: ${companyError.message || "未知錯誤"}`);
            return; // 停止執行，不繼續創建使用者
          }
        } else {
          console.log("公司已存在，直接創建使用者");
        }

        // 確保使用 trim 後的公司名稱
        const userValues = {
          ...values,
          company: companyName
        };
        
        console.log("準備調用 CreateUser API，數據:", userValues);
        
        // 創建使用者
        response = await User.CreateUser(userValues);
      }

      if (response.code === 200) {
        if (value) {
          setUsers((data) => data.map((user) => (user.id === response.data.id ? response.data : user)));
        } else {
          setUsers((data) => [...data, response.data]);
          // 如果創建了新公司，重新獲取公司列表確保父組件有最新數據
          if (createdNewCompany && refreshCompanies) {
            await refreshCompanies(); // 等待刷新完成
          }
        }
        message.success(`${value ? "更新" : "創建"}使用者成功`);
        form.resetFields(); // 先重置表單
        setVisible(false); // 再關閉 Modal
      } else {
        message.error(`${value ? "更新" : "創建"}失敗`);
      }
    } catch (error) {
      console.error("Submit failed:", error);
      // 顯示詳細的錯誤信息
      const errorMsg = error.response?.data?.message || error.message || "未知錯誤";
      message.error(`${value ? "更新" : "創建"}失敗: ${errorMsg}`);
    }
  };

  return (
    <Modal
      title={value ? "編輯使用者" : "新增使用者"}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button onClick={onCancel} key="back-button">
          返回
        </Button>,
        <Button
          onClick={confirm}
          hidden={!value} // 簡化條件表達式
          variant="filled"
          color="red"
          key="delete-user-button"
        >
          刪除使用者
        </Button>,
        <Button onClick={() => form.submit()} type="default" style={{ background: "var(--content-background)", color: "var(--primary-text)", border: "2px solid var(--primary-border)" }} key="finish-button">
          完成編輯
        </Button>,
      ]}
    >
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical" // 添加垂直布局
        preserve={false}
      >
        <Form.Item label="使用者姓名" name="name" rules={[{ required: true, message: "請輸入使用者姓名" }]}>
          <Input placeholder="請輸入使用者姓名" />
        </Form.Item>
        <Form.Item label="使用者權限" name="class" rules={[{ required: true, message: "請選擇使用者權限" }]}>
          <Select placeholder="請選擇使用者權限">
            <Option value={1}>唯讀</Option>
            <Option value={2}>可控制</Option>
            <Option value={3}>管理員</Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="使用者信箱"
          name="email"
          rules={[
            { required: true, message: "請輸入使用者信箱" },
            { type: "email", message: "請輸入有效的信箱格式" },
          ]}
        >
          <Input placeholder="請輸入使用者信箱" />
        </Form.Item>
        <Form.Item label="使用者帳號" name="account" rules={[{ required: true, message: "請輸入使用者帳號" }]}>
          <Input placeholder="請輸入使用者帳號" />
        </Form.Item>
        <Form.Item
          label="使用者密碼"
          name="password"
          rules={[{ required: !value, message: "請輸入使用者密碼" }]} // 編輯時密碼可選
        >
          <Input.Password placeholder="請輸入使用者密碼" />
        </Form.Item>
        <Form.Item label="使用者公司" name="company" rules={[{ required: true, message: "請輸入或選擇使用者公司名稱" }]}>
          {isManager ? (
            <AutoComplete
              options={companyOptions}
              placeholder="請輸入或選擇使用者公司名稱"
              filterOption={(inputValue, option) =>
                option?.value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
            />
          ) : (
            <Input disabled value={currentUserCompany} />
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
}
